import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiTrash2 } from 'react-icons/fi';
import { deleteContactSubmission, getContactSubmissions } from '../api/contactSubmissionsApi';
import ConfirmDialog from '../components/dashboard/ConfirmDialog';

const formatDate = (value) => {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleString();
};

const ContactSubmissionsPage = () => {
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [submissionToDelete, setSubmissionToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadSubmissions = async () => {
    setIsLoading(true);
    setError('');

    try {
      const data = await getContactSubmissions();
      setSubmissions(data);
    } catch (err) {
      setSubmissions([]);
      setError(err.message || 'Failed to load submissions.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSubmissions();
  }, []);

  const countryCodeOptions = useMemo(() => {
    const uniqueCodes = [
      ...new Set(submissions.map((item) => item.countryCode).filter(Boolean)),
    ].sort((a, b) => a.localeCompare(b));

    return [
      { value: '', label: 'All country codes' },
      ...uniqueCodes.map((value) => ({ value, label: value })),
    ];
  }, [submissions]);

  const filteredSubmissions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return submissions.filter((submission) => {
      if (countryCode && submission.countryCode !== countryCode) {
        return false;
      }

      if (query) {
        const haystack = `${submission.fullName} ${submission.email} ${submission.messagePreview}`.toLowerCase();
        if (!haystack.includes(query)) {
          return false;
        }
      }

      if (dateFrom) {
        const from = new Date(`${dateFrom}T00:00:00`);
        if (new Date(submission.createdAt) < from) {
          return false;
        }
      }

      if (dateTo) {
        const to = new Date(`${dateTo}T23:59:59.999`);
        if (new Date(submission.createdAt) > to) {
          return false;
        }
      }

      return true;
    });
  }, [submissions, searchQuery, countryCode, dateFrom, dateTo]);

  const hasActiveFilters = searchQuery || countryCode || dateFrom || dateTo;

  const clearFilters = () => {
    setSearchQuery('');
    setCountryCode('');
    setDateFrom('');
    setDateTo('');
  };

  const handleDeleteClick = (submission) => {
    setSubmissionToDelete(submission);
  };

  const handleDeleteCancel = () => {
    if (deletingId) {
      return;
    }

    setSubmissionToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!submissionToDelete) {
      return;
    }

    setDeletingId(submissionToDelete.id);
    setError('');

    try {
      await deleteContactSubmission(submissionToDelete.id);
      setSubmissions((current) => current.filter((item) => item.id !== submissionToDelete.id));
      setSubmissionToDelete(null);
    } catch (err) {
      setError(err.message || 'Failed to delete submission.');
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return <p className="text-sm text-[#6b635c]">Loading submissions…</p>;
  }

  return (
    <div className="mx-auto max-w-6xl">
      <header className="mb-6 border-b border-[#e4d9c8] pb-5">
        <p className="text-[12px] font-medium uppercase tracking-[0.16em] text-[#6b635c]">
          Submissions
        </p>
        <h2 className="mt-2 font-[Merriweather] text-[1.5rem] text-[#130705]">
          Contact messages
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#6b635c]">
          Review, filter, and manage messages submitted from the public contact form.
        </p>
      </header>

      {error ? (
        <p className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      ) : null}

      <section className="mb-5 rounded-md border border-[#e4d9c8] bg-white p-4 sm:p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-medium text-[#130705]">Filters</p>
          {hasActiveFilters ? (
            <button
              type="button"
              onClick={clearFilters}
              className="text-[13px] font-medium text-[#785E32] hover:underline"
            >
              Clear filters
            </button>
          ) : null}
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <label className="block md:col-span-2">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">Search</span>
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Name, email, or message"
              className="w-full rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-sm text-[#130705] outline-none focus:border-[#c9b48a]"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">
              Country code
            </span>
            <select
              value={countryCode}
              onChange={(event) => setCountryCode(event.target.value)}
              className="w-full rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-sm text-[#130705] outline-none focus:border-[#c9b48a]"
            >
              {countryCodeOptions.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">From date</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              className="w-full rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-sm text-[#130705] outline-none focus:border-[#c9b48a]"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">To date</span>
            <input
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
              className="w-full rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-sm text-[#130705] outline-none focus:border-[#c9b48a]"
            />
          </label>
        </div>

        <p className="mt-4 text-[12px] text-[#9a9288]">
          Showing {filteredSubmissions.length} of {submissions.length} submissions
        </p>
      </section>

      {submissions.length === 0 ? (
        <p className="text-sm text-[#6b635c]">No submissions yet.</p>
      ) : filteredSubmissions.length === 0 ? (
        <p className="text-sm text-[#6b635c]">No submissions match the current filters.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-[#e4d9c8] bg-white">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-[#e4d9c8] bg-[#faf8f4] text-[12px] uppercase tracking-[0.12em] text-[#6b635c]">
              <tr>
                <th className="px-4 py-3 font-medium">Sender</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Phone</th>
                <th className="px-4 py-3 font-medium">Message</th>
                <th className="px-4 py-3 font-medium">Submitted</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubmissions.map((submission) => (
                <tr key={submission.id} className="border-b border-[#f0e8dc] last:border-b-0">
                  <td className="px-4 py-3 font-medium text-[#130705]">{submission.fullName}</td>
                  <td className="px-4 py-3 text-[#4a4340]">{submission.email}</td>
                  <td className="px-4 py-3 text-[#4a4340]">
                    {submission.countryCode} {submission.phone}
                  </td>
                  <td className="max-w-xs px-4 py-3 text-[#4a4340]">
                    <span className="line-clamp-2">{submission.messagePreview}</span>
                  </td>
                  <td className="px-4 py-3 text-[#6b635c]">{formatDate(submission.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/dashboard/contact-submissions/${submission.id}`}
                        className="inline-flex items-center rounded-md border border-[#e4d9c8] bg-white px-3 py-1.5 text-[12px] font-medium text-[#785E32] transition hover:bg-[#faf8f4]"
                      >
                        View
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleDeleteClick(submission)}
                        disabled={deletingId === submission.id}
                        className="inline-flex items-center gap-1.5 rounded-md !border !border-[#e4d9c8] !bg-white px-3 py-1.5 text-[12px] font-medium !text-[#6b635c] !shadow-none transition hover:!border-[#fecdca] hover:!bg-[#fef3f2] hover:!text-[#b42318] hover:!translate-y-0 hover:!shadow-none disabled:opacity-60"
                      >
                        <FiTrash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        {deletingId === submission.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        isOpen={Boolean(submissionToDelete)}
        title="Delete submission?"
        message={
          submissionToDelete
            ? `Delete the message from ${submissionToDelete.fullName}? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={Boolean(deletingId)}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default ContactSubmissionsPage;
